/**
 * Engagement de Lojistas — Onboarding automático (Fase 3)
 * Configuração da sequência de boas-vindas (texto + áudio) enviada via
 * o MESMO WhatsApp já conectado na Prospecção.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Save, Send, Loader2, CheckCircle, AlertCircle, Mic, Upload,
  Trash2, Gift, Clock, RefreshCw, Play
} from 'lucide-react';

const dayLabel = (offset) => {
  if (offset === 0) return 'No cadastro (Dia 0)';
  if (offset === 1) return '1 dia depois';
  return `${offset} dias depois`;
};

export default function EngagementLojistas({ token, apiUrl }) {
  const API = apiUrl || '';
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // {type, text}
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testStep, setTestStep] = useState(0);
  const [sendingTest, setSendingTest] = useState(false);
  const [queue, setQueue] = useState({ items: [], stats: null });
  const fileInputs = useRef({});

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/master/engagement/onboarding`, { headers: authHeaders() });
      const data = await res.json();
      setConfig(data);
    } catch (e) {
      flash('error', 'Erro ao carregar configuração.');
    } finally {
      setLoading(false);
    }
  }, [API, authHeaders]);

  const loadQueue = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/master/engagement/queue`, { headers: authHeaders() });
      const data = await res.json();
      setQueue(data);
    } catch (e) { /* silent */ }
  }, [API, authHeaders]);

  useEffect(() => { loadConfig(); loadQueue(); }, [loadConfig, loadQueue]);

  const updateStep = (idx, patch) => {
    setConfig((prev) => {
      const steps = prev.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s));
      return { ...prev, steps };
    });
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/master/engagement/onboarding`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setConfig(data);
      flash('success', 'Configuração salva!');
    } catch (e) {
      flash('error', 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const handleAudioUpload = async (idx, file) => {
    if (!file) return;
    setUploadingIdx(idx);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/master/engagement/onboarding/audio`, {
        method: 'POST',
        headers: authHeaders(), // sem Content-Type: browser define boundary
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Falha no upload');
      updateStep(idx, { audio_url: data.url, audio_enabled: true });
      flash('success', 'Áudio enviado! Lembre de salvar a configuração.');
    } catch (e) {
      flash('error', e.message || 'Erro ao enviar áudio.');
    } finally {
      setUploadingIdx(null);
    }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) { flash('error', 'Informe um número.'); return; }
    setSendingTest(true);
    try {
      const res = await fetch(`${API}/api/master/engagement/test`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: testPhone.trim(), step_index: Number(testStep) }),
      });
      const data = await res.json();
      if (data.success) flash('success', 'Mensagem de teste enviada!');
      else flash('error', data.error || 'Não foi possível enviar.');
    } catch (e) {
      flash('error', 'Erro ao enviar teste.');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="animate-spin mr-2" size={20} /> Carregando...
      </div>
    );
  }
  if (!config) return null;

  const stats = queue.stats || {};

  return (
    <div className="space-y-6" data-testid="engagement-tab">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-lg bg-purple-500/20">
            <Gift className="text-purple-300" size={22} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Boas-vindas a novos lojistas</h3>
            <p className="text-gray-300 text-sm mt-1">
              Quando uma nova loja se cadastra, o sistema envia automaticamente uma sequência de
              mensagens (texto e/ou áudio) usando o <strong>mesmo WhatsApp conectado na Prospecção</strong>.
            </p>
          </div>
        </div>
        <label className="flex items-center gap-3 mt-4 cursor-pointer">
          <input
            type="checkbox"
            checked={!!config.enabled}
            onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
            className="w-5 h-5 accent-purple-500"
            data-testid="engagement-enabled-toggle"
          />
          <span className="text-white font-medium">Ativar onboarding automático</span>
        </label>
      </div>

      {/* Mensagem flash */}
      {msg && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm ${
          msg.type === 'success' ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                                  : 'bg-red-500/20 text-red-300 border border-red-500/30'}`}>
          {msg.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {msg.text}
        </div>
      )}

      {/* Passos */}
      <div className="space-y-4">
        {config.steps.map((step, idx) => (
          <div key={idx} className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-blue-400" />
                <span className="text-white font-semibold">{dayLabel(step.day_offset)}</span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!step.enabled}
                  onChange={(e) => updateStep(idx, { enabled: e.target.checked })}
                  className="w-4 h-4 accent-blue-500"
                />
                <span className="text-gray-300 text-sm">Ativo</span>
              </label>
            </div>

            <textarea
              value={step.text}
              onChange={(e) => updateStep(idx, { text: e.target.value })}
              rows={4}
              placeholder="Texto da mensagem..."
              className="w-full bg-gray-900/70 border border-gray-700 rounded-lg px-3 py-2 text-gray-100 text-sm focus:outline-none focus:border-blue-500 resize-y"
            />
            <p className="text-gray-500 text-xs mt-1">
              Variáveis disponíveis: <code className="text-blue-300">{'{nome_loja}'}</code>
            </p>

            {/* Áudio */}
            <div className="mt-4 border-t border-gray-700/50 pt-4">
              <div className="flex flex-wrap items-center gap-3">
                <Mic size={16} className="text-purple-400" />
                <span className="text-gray-300 text-sm">Áudio (nota de voz)</span>
                <label className="flex items-center gap-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 text-xs px-3 py-1.5 rounded-lg cursor-pointer transition-colors">
                  {uploadingIdx === idx ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {step.audio_url ? 'Trocar áudio' : 'Enviar áudio'}
                  <input
                    ref={(el) => (fileInputs.current[idx] = el)}
                    type="file"
                    accept=".ogg,.mp3,.m4a,.opus,.aac,.wav,audio/*"
                    className="hidden"
                    onChange={(e) => handleAudioUpload(idx, e.target.files?.[0])}
                  />
                </label>
                {step.audio_url && (
                  <>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!step.audio_enabled}
                        onChange={(e) => updateStep(idx, { audio_enabled: e.target.checked })}
                        className="w-4 h-4 accent-purple-500"
                      />
                      <span className="text-gray-300 text-xs">Enviar áudio</span>
                    </label>
                    <button
                      onClick={() => updateStep(idx, { audio_url: '', audio_enabled: false })}
                      className="text-red-400 hover:text-red-300"
                      title="Remover áudio"
                    >
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
              </div>
              {step.audio_url && (
                <audio controls src={step.audio_url} className="mt-3 w-full max-w-sm h-9">
                  <track kind="captions" />
                </audio>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Salvar */}
      <div className="flex justify-end">
        <button
          onClick={saveConfig}
          disabled={saving}
          data-testid="engagement-save-btn"
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-5 py-2.5 rounded-lg font-medium transition-colors"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Salvar configuração
        </button>
      </div>

      {/* Teste */}
      <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-5">
        <h4 className="text-white font-semibold flex items-center gap-2 mb-3">
          <Send size={16} className="text-green-400" /> Testar envio
        </h4>
        <p className="text-gray-400 text-sm mb-3">
          Envia um passo para o número informado (requer o WhatsApp da Prospecção conectado).
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            placeholder="Número com DDD (ex: 11999998888)"
            className="flex-1 bg-gray-900/70 border border-gray-700 rounded-lg px-3 py-2 text-gray-100 text-sm focus:outline-none focus:border-green-500"
          />
          <select
            value={testStep}
            onChange={(e) => setTestStep(e.target.value)}
            className="bg-gray-900/70 border border-gray-700 rounded-lg px-3 py-2 text-gray-100 text-sm focus:outline-none focus:border-green-500"
          >
            {config.steps.map((s, i) => (
              <option key={i} value={i}>{dayLabel(s.day_offset)}</option>
            ))}
          </select>
          <button
            onClick={sendTest}
            disabled={sendingTest}
            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white px-5 py-2.5 rounded-lg font-medium transition-colors"
          >
            {sendingTest ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
            Enviar teste
          </button>
        </div>
      </div>

      {/* Histórico / Fila */}
      <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-white font-semibold">Fila &amp; histórico</h4>
          <button onClick={loadQueue} className="text-gray-400 hover:text-white flex items-center gap-1 text-sm">
            <RefreshCw size={14} /> Atualizar
          </button>
        </div>
        {stats && (
          <div className="grid grid-cols-4 gap-3 mb-4">
            {['pending', 'sent', 'failed', 'skipped'].map((k) => (
              <div key={k} className="bg-gray-900/60 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-white">{stats[k] ?? 0}</p>
                <p className="text-gray-400 text-xs capitalize">{k}</p>
              </div>
            ))}
          </div>
        )}
        <div className="max-h-64 overflow-y-auto space-y-2">
          {(queue.items || []).length === 0 && (
            <p className="text-gray-500 text-sm text-center py-6">Nenhuma mensagem na fila ainda.</p>
          )}
          {(queue.items || []).map((it) => (
            <div key={it.id} className="flex items-center justify-between bg-gray-900/50 rounded-lg px-3 py-2 text-sm">
              <div className="min-w-0">
                <p className="text-gray-200 truncate">{it.restaurant_name || it.tenant_id}</p>
                <p className="text-gray-500 text-xs">
                  {dayLabel(it.day_offset)} · {String(it.scheduled_at || '').slice(0, 10)}
                </p>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${
                it.status === 'sent' ? 'bg-green-500/20 text-green-300'
                : it.status === 'failed' ? 'bg-red-500/20 text-red-300'
                : it.status === 'skipped' ? 'bg-gray-500/20 text-gray-300'
                : 'bg-yellow-500/20 text-yellow-300'}`}>
                {it.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
